import { careIndustryPageKeys } from './content/careIndustryLabels.js';
import type { LandingLocale } from './content/landingContentTypes.js';
import { publicIndustryPageKeys, industryPageLabel } from './content/industryPages.js';

export const PUBLIC_LANDING_PATHS = [
  ...careIndustryPageKeys.map(key=>`/branchen/${key}` as const),
  '/',
  '/bewerben',
  '/unternehmen',
  '/branchen/pflege',
  '/branchen/handwerk',
  '/branchen/einzelhandel',
  '/branchen/elektriker',
  '/branchen/bauwesen',
  '/branchen/schweisser',
  '/branchen/cnc',
  '/branchen/shk',
  '/branchen/lkw-fahrer',
  '/branchen/gastronomie', '/branchen/baecker', '/branchen/metzger', '/branchen/frischetheke',
  '/globus',
  '/ablauf',
  '/ueber-vivaporta',
  '/team',
  '/kontakt',
  '/impressum',
] as const;

export type PublicLandingPath = (typeof PUBLIC_LANDING_PATHS)[number];

export const normalizeLandingPath = (path: string): string => path.replace(/\/+$/u, '') || '/';

export const isPublicLandingPath = (path: string): boolean =>
  (PUBLIC_LANDING_PATHS as readonly string[]).includes(normalizeLandingPath(path));

export const LANDING_PAGE_TITLES: Record<LandingLocale, Record<PublicLandingPath, string>> = {
  de: {
    ...Object.fromEntries(publicIndustryPageKeys.map(key => [`/branchen/${key}`, `${industryPageLabel(key, 'de')} in Deutschland`])) as Record<`/branchen/${typeof publicIndustryPageKeys[number]}`, string>,
    '/bewerben': 'Bewirb dich jetzt',
    '/': 'Internationale Fachkräfte für Deutschland',
    '/unternehmen': 'Fachkräfte für Unternehmen',
    '/branchen/pflege': 'Internationale Pflegefachkräfte',
    '/branchen/handwerk': 'Internationale Fachkräfte im Handwerk',
    '/branchen/einzelhandel': 'Arbeiten im Einzelhandel',
    '/globus': 'Arbeiten bei Globus',
    '/ablauf': 'Ablauf',
    '/ueber-vivaporta': 'Mission',
    '/team': 'Unser Team',
    '/kontakt': 'Kontakt',
    '/impressum': 'Impressum',
  },
  en: {
    ...Object.fromEntries(publicIndustryPageKeys.map(key => [`/branchen/${key}`, `${industryPageLabel(key, 'en')} in Germany`])) as Record<`/branchen/${typeof publicIndustryPageKeys[number]}`, string>,
    '/bewerben': 'Apply now',
    '/': 'International Professionals for Germany',
    '/unternehmen': 'International Recruitment for Companies',
    '/branchen/pflege': 'International Care Professionals',
    '/branchen/handwerk': 'International Skilled Workers for Trades',
    '/branchen/einzelhandel': 'Working in Retail',
    '/globus': 'Work at Globus',
    '/ablauf': 'Process',
    '/ueber-vivaporta': 'Mission',
    '/team': 'Our Team',
    '/kontakt': 'Contact',
    '/impressum': 'Legal Notice',
  },
  tr: {
    ...Object.fromEntries(publicIndustryPageKeys.map(key => [`/branchen/${key}`, `Almanya’da ${industryPageLabel(key, 'tr')}`])) as Record<`/branchen/${typeof publicIndustryPageKeys[number]}`, string>,
    '/bewerben': 'Hemen başvur',
    '/': 'Almanya İçin Uluslararası Çalışanlar',
    '/unternehmen': 'Şirketler İçin Uluslararası Çalışanlar',
    '/branchen/pflege': 'Uluslararası Bakım Uzmanları',
    '/branchen/handwerk': 'Zanaat Alanında Uluslararası Çalışanlar',
    '/branchen/einzelhandel': 'Perakendede Çalışmak',
    '/globus': 'Globus’ta Çalış',
    '/ablauf': 'Süreç',
    '/ueber-vivaporta': 'Misyon',
    '/team': 'Ekibimiz',
    '/kontakt': 'İletişim',
    '/impressum': 'Künye',
  },
};

export const landingPageTitle = (locale: LandingLocale, path: string): string | undefined => {
  const normalizedPath = normalizeLandingPath(path);
  if (!isPublicLandingPath(normalizedPath)) return undefined;
  return LANDING_PAGE_TITLES[locale][normalizedPath as PublicLandingPath];
};

export const LANDING_NOT_FOUND_TITLES: Record<LandingLocale, string> = {
  de: 'Seite nicht gefunden',
  en: 'Page not found',
  tr: 'Sayfa bulunamadı',
};
